// « Encaissement » — the desk's payment log (P3-T).
//
// `docs/PMS-TERMINAL-LINK-BRIEF.md` §8.6/§8.7. Replaces the paper log at
// reception: type a room, type an amount, pick a tender, done — and for card,
// the terminal itself supplies the amount that gets recorded.
//
// The keyboard shape is deliberate and was validated interactively with the
// operator — it is not a UI to redesign:
//   1. Room field, Return → lookupRoom. Not found → a short beep, text stays
//      selected for retry (the muscle memory of the Vega terminal already in
//      use at the desk).
//   2. Amount field, Return with NO tender chosen yet → blur only (arms the
//      tender shortcuts). Return with a tender ALREADY chosen → submit.
//      Digit keys 1-9 are NEVER read from this field's own onKeyDown — typing
//      "125" must never be hijacked by the "2 Espèces" shortcut mid-entry.
//   3. A global listener maps 1-5 to tender selection AND immediate submit,
//      but only while focus has genuinely left the room/amount fields
//      (1-7 on « Suivi des paiements », which adds Virement and VAD).
//
// « Arrivées » and « Départs » (§8.8/§8.8g) sit under the form, each in two
// sections with every payment listed. Each arriving or departing booking can
// PREFILL this form — room (or résa) and what is left to collect — and a split
// payment (two cards, card + ANCV, cash…) carries the remainder into the next
// tender. The gestures above are unchanged; the prefill only types for the desk.
//
// §8.8k (operator 2026-09-26): the Journal is gone. Its jobs are on the sheets
// — every payment taken here shows on its booking with a red ⚠ until the feed
// reads it in Vega (replacing « Marquer saisi »), « Régler » / « Annuler » of
// an « en attente » sit on the row that carries it, and the day's other
// payments and total close the Arrivées sheet.
//
// « Suivi des paiements » (§8.8r, operator 2026-10-24) lists every booking with
// its payment status. Its rows open the form on their résa — « Arrhes » for one
// to come, the desk typing what was received — and on that tab only the form
// also takes « 6 Virement » and « 7 VAD », the way arrhes mostly arrive. The
// room field resolves a résa whatever its dates (20261024100100).

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type {
  IsoDate,
  ReceptionPaymentId,
  RoomStayLookup,
  TenderType,
  Terminal,
  TerminalTransactionResult,
} from '@lemontaigu/pms-core';
import { CAPABILITIES } from '@lemontaigu/platform';
import { useCapability } from '@lemontaigu/platform-ui';
import { PerEtablissement, usePms, usePmsScope } from './PmsProvider.js';
import { fmtAmount, parseEurosToCents, roomLookupOutcome } from './logic.js';
import { Departures } from './Departures.js';
import { Arrivals } from './Arrivals.js';
import { Journee } from './Journee.js';
import { Unissued } from './Unissued.js';
import { Debtors } from './Debtors.js';
import { Controles } from './Controles.js';
import { PaymentFollowUp, type FollowUpBill } from './PaymentFollowUp.js';
import { CardFindingsFlags, useCardFindingsAcross, useCardFindingsDay } from './CardFindingsContext.js';
import { CardTapsSection } from './CardTapsSection.js';
import { leadCount } from './cardFindingsView.js';
import {
  centsToInput,
  shiftDay,
  todayParis,
  remainderAfter,
  targetLabel,
  TENDER_LABEL_FR,
  type SuspenseToSettle,
} from './departureSheet.js';

// ── Small local helpers ──────────────────────────────────────────────────────

/** A short, unmistakable "nope" — the muscle-memory match for the Vega
 *  terminal's own beep on an unresolved room. Web Audio, ~200ms, no asset. */
function beep(): void {
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.frequency.value = 440;
    gain.gain.value = 0.15;
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.2);
    osc.onended = () => void ctx.close();
  } catch {
    // A missing/blocked audio context is not worth failing the screen over —
    // the visual "introuvable" state still carries the information.
  }
}

interface TenderKey {
  key: '1' | '2' | '3' | '4' | '5' | '6' | '7';
  type: SelectableTender;
  label: string;
}

const TENDERS: TenderKey[] = [
  { key: '1', type: 'card', label: '1 Carte' },
  { key: '2', type: 'cash', label: '2 Espèces' },
  { key: '3', type: 'cheque', label: '3 Chèque' },
  { key: '4', type: 'ancv', label: '4 ANCV' },
  { key: '5', type: 'suspense', label: '5 En attente' },
];

/** « Suivi des paiements » adds the two ways arrhes mostly arrive (operator
 *  2026-10-24) — after the five the desk's fingers know, never instead. */
const FOLLOW_UP_TENDERS: TenderKey[] = [
  ...TENDERS,
  { key: '6', type: 'transfer', label: '6 Virement' },
  { key: '7', type: 'vad', label: '7 VAD' },
];

/** 'notAsked': the terminal never had the request — a retry is safe.
 *  'notDone': the terminal answered it did not carry it out — no debit.
 *  'unknown': no answer, or one that does not rule out a debit — check the
 *  TPE before any retry (§8.8m, §8.8q). */
type CardPhase = 'idle' | 'waiting' | 'authorised' | 'refused' | 'notAsked' | 'notDone' | 'unknown';

/** The bridge stamps its heartbeat ≤ every 15 s; past this, it is not there. */
const BRIDGE_STALE_MS = 60_000;
const bridgeLive = (t: Terminal | null): boolean =>
  !!t?.bridgeSeenAt && Date.now() - new Date(t.bridgeSeenAt).getTime() < BRIDGE_STALE_MS;

/** An arrival, a departure or a booking being settled, possibly in several
 *  payments — or arrhes being taken, one payment of the amount received. */
interface BillTarget {
  /** What was typed in « Chambre ou résa ». */
  target: string;
  kind: 'arrival' | 'departure' | FollowUpBill;
  remainingCents: number;
}

/** The tenders this screen can select — everything but 'void', which only
 *  ever comes from `cancelSuspense` and is never chosen by hand. */
type SelectableTender = Exclude<TenderType, 'void'>;

/** « Contrôles »: ONE établissement's findings and card taps (U3) — chosen in
 *  the page when several are in reach, and read inside that choice. */
function ControlesView({
  day,
  onDay,
  today,
  nav,
}: {
  day: IsoDate;
  onDay: (d: IsoDate) => void;
  today: IsoDate;
  nav: ReactNode;
}) {
  const state = useCardFindingsDay(day);
  return (
    <>
      <Controles day={day} onDay={onDay} today={today} state={state} nav={nav} />
      <CardTapsSection today={today} />
    </>
  );
}

export function Encaissement() {
  const { receptionPayments, departures, arrivals, takings } = usePms();
  const canWrite = useCapability(CAPABILITIES.PMS_PAYMENT_WRITE);

  const roomRef = useRef<HTMLInputElement>(null);
  const amountRef = useRef<HTMLInputElement>(null);

  const [terminal, setTerminal] = useState<Terminal | null>(null);
  // Bumped after every write here, so the sheet on screen re-reads at once.
  const [refreshKey, setRefreshKey] = useState(0);
  const [sheetError, setSheetError] = useState<string | null>(null);

  const [roomInput, setRoomInput] = useState('');
  const [lookup, setLookup] = useState<RoomStayLookup | null>(null);
  // U0: the number matched in several établissements today — the desk picks
  // one; the payment is never filed against the first match.
  const [choices, setChoices] = useState<readonly RoomStayLookup[]>([]);
  const { properties } = usePmsScope();
  const propertyLabel = (code: string) => properties.find((p) => p.code === code)?.labelFr ?? code;
  const [searched, setSearched] = useState(false);
  const [amountInput, setAmountInput] = useState('');
  const [tender, setTender] = useState<SelectableTender | null>(null);
  const [settlingSuspenseId, setSettlingSuspenseId] = useState<ReceptionPaymentId | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardPhase, setCardPhase] = useState<CardPhase>('idle');
  const [cardOutcome, setCardOutcome] = useState<TerminalTransactionResult | null>(null);
  // « Arrivées » first — the day's payments are taken there (§8.8k); the
  // Journal that stood third until 2026-09-26 is gone, its jobs on the sheets.
  // « Clients débiteurs » (§8.8l, 2026-09-26) sits beside « Non facturés ».
  // « Suivi des paiements » (§8.8r, 2026-10-24) after them: every booking.
  const [view, setView] = useState<
    'arrivals' | 'departures' | 'journee' | 'unissued' | 'debtors' | 'balances' | 'controles'
  >('arrivals');
  const tenders = view === 'balances' ? FOLLOW_UP_TENDERS : TENDERS;
  // « Contrôles » (L0b): findings belong to a PAYMENT day, so they are read here
  // once and shared. Default: the day just ended, the one Vega has not closed yet
  // (today's cannot be judged before tomorrow's 04:30 télécollecte poll).
  const today = todayParis(new Date());
  const [findingsDay, setFindingsDay] = useState<IsoDate>(() => shiftDay(today, -1));
  // The flags on the sheets: every établissement's findings, since the sheets
  // list every building's rows (U3). « Contrôles » reads its own below.
  const flags = useCardFindingsAcross(findingsDay);
  // §8.8p — the tab a payment is taken on is its shift (operator 2026-09-27).
  const takenOn = view === 'arrivals' ? 'checkin' : view === 'departures' ? 'checkout' : null;
  const [billTarget, setBillTarget] = useState<BillTarget | null>(null);
  const refreshSheets = useCallback(() => setRefreshKey((k) => k + 1), []);

  // Re-read every 30 s: « 1 Carte » follows the bridge's heartbeat, so a
  // stopped or crashed bridge turns the key off by itself (§8.8q).
  const [terminalLive, setTerminalLive] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const load = () =>
      void receptionPayments.terminals().then((r) => {
        if (cancelled) return;
        const t = r.ok && r.value.length > 0 ? r.value[0] : null;
        setTerminal(t);
        setTerminalLive(bridgeLive(t));
      });
    load();
    const timer = setInterval(load, 30_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [receptionPayments]);

  const resetForm = useCallback(() => {
    setRoomInput('');
    setLookup(null);
    setChoices([]);
    setSearched(false);
    setAmountInput('');
    setTender(null);
    setSettlingSuspenseId(null);
    setError(null);
    setCardPhase('idle');
    setCardOutcome(null);
    setBillTarget(null);
    roomRef.current?.focus();
  }, []);

  const runLookup = useCallback(
    async (room: string) => {
      const trimmed = room.trim();
      if (trimmed === '') return;
      setError(null);
      const r = await receptionPayments.lookupRoom(trimmed);
      setSearched(true);
      if (!r.ok) {
        setError(r.error.message ?? 'recherche impossible');
        return;
      }
      const outcome = roomLookupOutcome(r.value);
      if (outcome.kind === 'none') {
        setLookup(null);
        setChoices([]);
        beep();
        // Keep focus in the field, text selected — the retry gesture matches
        // the Vega terminal the desk already knows.
        roomRef.current?.focus();
        roomRef.current?.select();
        return;
      }
      if (outcome.kind === 'choose') {
        setLookup(null);
        setChoices(outcome.stays);
        return;
      }
      setChoices([]);
      setLookup(outcome.stay);
      // Amount reveals once a stay is found; move focus straight to it.
      requestAnimationFrame(() => amountRef.current?.focus());
    },
    [receptionPayments],
  );

  /** The desk's answer to « which établissement? » — then the usual flow. */
  const chooseStay = (stay: RoomStayLookup) => {
    setChoices([]);
    setLookup(stay);
    requestAnimationFrame(() => amountRef.current?.focus());
  };

  const onRoomKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void runLookup(roomInput);
    }
  };

  const onAmountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Deliberately NOT reading digit keys here — see the file header. Return
    // is the only key this field's own handler ever acts on.
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (tender === null) {
      // Arms the global 1-5 shortcuts; does not submit.
      amountRef.current?.blur();
    } else {
      void submit(tender);
    }
  };

  /** After a payment is recorded: settle, or carry the remainder of a
   *  departure into the next tender (a split — two cards, card + ANCV…).
   *  An « en attente » defers the rest, so it ends the split. */
  const afterRecorded = async (paidCents: number, deferred: boolean) => {
    refreshSheets();
    // Arrhes are the amount received, once — nothing is « left » to carry.
    const left =
      billTarget && !deferred && billTarget.kind !== 'arrhes' ? remainderAfter(billTarget.remainingCents, paidCents) : null;
    if (billTarget && left !== null) {
      setBillTarget({ ...billTarget, remainingCents: left });
      setAmountInput(centsToInput(left));
      setTender(null);
      setSettlingSuspenseId(null);
      setCardPhase('idle');
      setCardOutcome(null);
      setError(null);
      requestAnimationFrame(() => amountRef.current?.focus());
      return;
    }
    resetForm();
  };

  /** From « Arrivées » or « Départs »: type the room (or résa) and the amount
   *  for the desk. */
  const prefill = (target: string, amountCents: number, kind: BillTarget['kind']) => {
    setSettlingSuspenseId(null);
    setTender(null);
    setError(null);
    setCardPhase('idle');
    setCardOutcome(null);
    setRoomInput(target);
    setAmountInput(amountCents > 0 ? centsToInput(amountCents) : '');
    setBillTarget(amountCents > 0 ? { target, kind, remainingCents: amountCents } : null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    void runLookup(target);
  };

  /** From « Suivi des paiements »: the résa, and what is owed — or, for
   *  arrhes, no amount: the desk types what was received. */
  const prefillBooking = (resa: string, kind: FollowUpBill, amountCents: number) => {
    setSettlingSuspenseId(null);
    setTender(null);
    setError(null);
    setCardPhase('idle');
    setCardOutcome(null);
    setRoomInput(resa);
    setAmountInput(amountCents > 0 ? centsToInput(amountCents) : '');
    setBillTarget({ target: resa, kind, remainingCents: Math.max(amountCents, 0) });
    window.scrollTo({ top: 0, behavior: 'smooth' });
    void runLookup(resa);
  };

  const submit = useCallback(
    async (chosenTender: SelectableTender) => {
      if (!canWrite || submitting || !lookup) return;
      const amountCents = parseEurosToCents(amountInput);
      if (amountCents === null || amountCents <= 0) {
        setError('Montant invalide.');
        return;
      }
      setError(null);
      setSubmitting(true);

      if (chosenTender === 'card') {
        if (!terminal) {
          setSubmitting(false);
          setError('Aucun terminal de paiement disponible.');
          return;
        }
        // The heartbeat may have gone stale since the last 30 s read.
        const fresh = await receptionPayments.terminals();
        const current = fresh.ok ? (fresh.value.find((t) => t.id === terminal.id) ?? null) : null;
        if (!bridgeLive(current)) {
          setSubmitting(false);
          setTerminalLive(false);
          setError('Le TPE n’est pas relié au PMS — encaissez directement sur le TPE.');
          return;
        }
        const intentR = await receptionPayments.createTerminalIntent({
          terminalId: terminal.id,
          outlet: 'reception',
          amountCents,
          roomNumber: roomInput.trim(),
          resa: lookup.resa,
          propertyCode: lookup.propertyCode,
        });
        if (!intentR.ok) {
          setSubmitting(false);
          setError(intentR.error.message ?? 'impossible de préparer le terminal');
          return;
        }
        setCardPhase('waiting');
        const txnR = await receptionPayments.awaitTerminalResult(intentR.value);
        if (!txnR.ok) {
          setSubmitting(false);
          setCardPhase(txnR.error.retryable ? 'notAsked' : 'unknown');
          return;
        }
        setCardOutcome(txnR.value);
        // Only money that moved is recorded (the RPC refuses the rest anyway).
        if (txnR.value.status !== 'authorised' && txnR.value.status !== 'offline_approved') {
          setSubmitting(false);
          setCardPhase(
            txnR.value.status === 'refused' ? 'refused' : txnR.value.status === 'not_done' ? 'notDone' : 'unknown',
          );
          return;
        }
        setCardPhase('authorised');
        // The amount recorded is the TERMINAL'S, never the typed value —
        // enforced again server-side, but this call never sends its own.
        const recordR = await receptionPayments.recordPayment({
          tenderType: 'card',
          roomNumber: roomInput.trim(),
          resa: lookup.resa,
          terminalTransactionId: txnR.value.id,
          settlesSuspenseId: settlingSuspenseId,
          propertyCode: lookup.propertyCode,
          takenOn,
        });
        setSubmitting(false);
        if (!recordR.ok) {
          setError(recordR.error.message ?? "échec de l'enregistrement");
          return;
        }
        await afterRecorded(txnR.value.amountCents, false);
        return;
      }

      const recordR = await receptionPayments.recordPayment({
        tenderType: chosenTender,
        roomNumber: roomInput.trim(),
        resa: lookup.resa,
        amountCents,
        settlesSuspenseId: settlingSuspenseId,
        propertyCode: lookup.propertyCode,
        takenOn,
      });
      setSubmitting(false);
      if (!recordR.ok) {
        setError(recordR.error.message ?? "échec de l'enregistrement");
        return;
      }
      await afterRecorded(amountCents, chosenTender === 'suspense');
    },
    [
      canWrite,
      submitting,
      lookup,
      amountInput,
      terminal,
      roomInput,
      settlingSuspenseId,
      receptionPayments,
      refreshSheets,
      resetForm,
      billTarget,
    ],
  );

  const selectTender = useCallback(
    (t: SelectableTender, submitImmediately: boolean) => {
      setTender(t);
      if (submitImmediately) void submit(t);
    },
    [submit],
  );

  // The global 1-5 listener — inactive while focus is genuinely in the room
  // or amount field, which is what makes it safe to let Return-in-amount
  // "arm" it via blur rather than something more elaborate.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const active = document.activeElement;
      if (active === roomRef.current || active === amountRef.current) return;
      if (!lookup || submitting) return;
      const match = tenders.find((t) => t.key === e.key);
      if (!match) return;
      e.preventDefault();
      selectTender(match.type, true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lookup, submitting, selectTender, tenders]);

  // A tender only the other tab offers is not left chosen on this one.
  useEffect(() => {
    if (tender && !tenders.some((t) => t.type === tender)) setTender(null);
  }, [tender, tenders]);

  /** « Régler » an « en attente » from a sheet: the form opens on it, with
   *  what the guest should pay, and the payment recorded is linked to it. */
  const settleSuspense = (s: SuspenseToSettle) => {
    if (!s.target) return;
    setTender(null);
    setError(null);
    setCardPhase('idle');
    setCardOutcome(null);
    setBillTarget(null);
    setSettlingSuspenseId(s.id);
    setRoomInput(s.target);
    setAmountInput(centsToInput(s.cents));
    window.scrollTo({ top: 0, behavior: 'smooth' });
    void runLookup(s.target);
  };

  /** « Annuler » an « en attente »: closes it without money moving — a void
   *  row, never an edit (append-only), so it asks first. */
  const cancelSuspense = async (id: ReceptionPaymentId) => {
    if (!window.confirm('Annuler cette mise en attente ? Aucun paiement ne sera enregistré.')) return;
    setSheetError(null);
    const r = await receptionPayments.cancelSuspense(id);
    if (!r.ok) {
      setSheetError(r.error.message ?? 'annulation impossible');
      return;
    }
    refreshSheets();
  };

  // The form: a slot of the sheet on screen, under its title (§8.8k).
  const form = (
    <>
      {!canWrite && <p className="pms-warn">Lecture seule — la saisie demande le droit « encaissement ».</p>}
      {sheetError && <p className="pms-warn">⚠ {sheetError}</p>}

      <section className="pms-form">
        {settlingSuspenseId !== null && (
          <p className="pms-hint">
            Règlement d’une mise en attente —{' '}
            <button type="button" className="pms-link" onClick={() => setSettlingSuspenseId(null)}>
              annuler le rattachement
            </button>
          </p>
        )}
        {billTarget && billTarget.kind === 'arrhes' && (
          <p className="pms-hint">
            Arrhes {targetLabel(billTarget.target)} — saisissez le montant reçu, puis le mode (6 Virement, 7 VAD…).{' '}
            <button type="button" className="pms-link" onClick={() => setBillTarget(null)}>
              arrêter
            </button>
          </p>
        )}
        {billTarget && billTarget.kind !== 'arrhes' && (
          <p className="pms-hint">
            {billTarget.kind === 'arrival' ? 'Arrivée' : billTarget.kind === 'departure' ? 'Départ' : 'Réservation'}{' '}
            {targetLabel(billTarget.target)} — reste à encaisser <strong>{fmtAmount(billTarget.remainingCents)}</strong>.
            Paiement en plusieurs fois : validez chaque mode, le reste se reporte.{' '}
            <button type="button" className="pms-link" onClick={() => setBillTarget(null)}>
              arrêter
            </button>
          </p>
        )}
        <div className="pms-form-grid">
          <label
            className="pms-field"
            title={`Chambre ou résa, Entrée · montant, Entrée · touche 1-${tenders.length} pour le mode de règlement`}
          >
            <span>Chambre ou résa</span>
            <input
              ref={roomRef}
              type="text"
              value={roomInput}
              disabled={!canWrite || submitting}
              onChange={(e) => {
                setRoomInput(e.target.value);
                setSearched(false);
                setLookup(null);
                setChoices([]);
                setBillTarget(null);
              }}
              onKeyDown={onRoomKeyDown}
              autoFocus
              placeholder="ex. 214"
            />
          </label>

          {searched && !lookup && choices.length === 0 && (
            <p className="pms-field pms-field-wide pms-warn" role="alert">
              Introuvable : aucune chambre occupée aujourd’hui ni réservation à ce numéro.
            </p>
          )}

          {choices.length > 1 && (
            <div className="pms-field pms-field-wide" role="group" aria-label="Établissement">
              <span>Ce numéro existe dans plusieurs établissements aujourd’hui — lequel ?</span>
              <div className="pms-chips">
                {choices.map((s) => (
                  <button
                    key={s.propertyCode}
                    type="button"
                    className="pms-chip"
                    disabled={!canWrite || submitting}
                    onClick={() => chooseStay(s)}
                  >
                    {propertyLabel(s.propertyCode)} · {s.partyLabel} · Résa {s.resa}
                  </button>
                ))}
              </div>
            </div>
          )}

          {lookup && (
            <>
              <div className="pms-field">
                <span>Client</span>
                <strong>{lookup.partyLabel}</strong>
                <span className="pms-sub">Résa {lookup.resa}</span>
              </div>

              <label className="pms-field">
                <span>Montant</span>
                <input
                  ref={amountRef}
                  type="text"
                  inputMode="decimal"
                  value={amountInput}
                  disabled={!canWrite || submitting}
                  onChange={(e) => setAmountInput(e.target.value)}
                  onKeyDown={onAmountKeyDown}
                  placeholder="0,00"
                />
              </label>
            </>
          )}
        </div>

        {lookup && (
          <div className="pms-chips" role="group" aria-label="Mode de règlement">
            {tenders.map((t) => (
              <button
                key={t.type}
                type="button"
                className={`pms-chip${tender === t.type ? ' is-active' : ''}`}
                disabled={!canWrite || submitting || (t.type === 'card' && !terminalLive)}
                title={
                  t.type === 'card' && !terminalLive
                    ? 'TPE non relié au PMS — encaissez directement sur le TPE'
                    : undefined
                }
                onClick={() => selectTender(t.type, false)}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {lookup && (
          <div className="pms-form-actions">
            {error && (
              <span className="auth-err" role="alert">
                {error}
              </span>
            )}
            <span className="pms-total">
              {tender ? TENDER_LABEL_FR[tender] : 'Choisir un mode de règlement'}
            </span>
            <button
              type="button"
              className="auth-btn"
              disabled={!canWrite || submitting || !tender}
              onClick={() => tender && void submit(tender)}
            >
              {submitting ? 'Encaissement…' : 'Valider'}
            </button>
          </div>
        )}

        {cardPhase === 'waiting' && (
          <p className="pms-warn">En attente du TPE… présentez la carte.</p>
        )}
        {cardPhase === 'authorised' && cardOutcome && (
          <p className="pms-hint">
            ✓ Autorisée{cardOutcome.authorisationRef ? ` — n° ${cardOutcome.authorisationRef}` : ''}
            {cardOutcome.cardMasked ? ` (${cardOutcome.cardMasked})` : ''}
          </p>
        )}
        {cardPhase === 'refused' && (
          <p className="pms-warn" role="alert">
            ✗ Carte refusée par le terminal.
          </p>
        )}
        {cardPhase === 'notAsked' && (
          <p className="pms-warn" role="alert">
            Le TPE n’a pas reçu la demande — aucun débit. Vous pouvez réessayer, ou encaisser sur le TPE seul.
          </p>
        )}
        {cardPhase === 'notDone' && (
          <p className="pms-warn" role="alert">
            Le TPE n’a pas effectué le paiement — aucun débit. Vous pouvez réessayer.
          </p>
        )}
        {cardPhase === 'unknown' && (
          <p className="pms-warn" role="alert">
            ⚠ Résultat inconnu : le TPE n’a pas confirmé le paiement. Vérifiez son dernier ticket avant tout
            nouvel essai — la carte a pu être débitée.
          </p>
        )}
        {lookup && !terminalLive && cardPhase === 'idle' && (
          <p className="pms-hint">« Carte » indisponible : le TPE n’est pas relié au PMS — encaissez directement sur le TPE.</p>
        )}
      </section>
    </>
  );

  // The view chips, at the top of whichever sheet is on screen (§8.8k:
  // the page title gave its place to them, operator 2026-09-26).
  const nav = (
    <div className="pms-chips pms-viewnav" role="group" aria-label="Vue">
      {/* Départs first: they come first in the day (operator 2026-10-01). */}
      <button
        type="button"
        className={`pms-chip${view === 'departures' ? ' is-active' : ''}`}
        onClick={() => setView('departures')}
      >
        Départs
      </button>
      <button
        type="button"
        className={`pms-chip${view === 'arrivals' ? ' is-active' : ''}`}
        onClick={() => setView('arrivals')}
      >
        Arrivées
      </button>
      <button
        type="button"
        className={`pms-chip${view === 'journee' ? ' is-active' : ''}`}
        onClick={() => setView('journee')}
      >
        Journée
      </button>
      <button
        type="button"
        className={`pms-chip${view === 'unissued' ? ' is-active' : ''}`}
        onClick={() => setView('unissued')}
      >
        Non facturés
      </button>
      <button
        type="button"
        className={`pms-chip${view === 'debtors' ? ' is-active' : ''}`}
        onClick={() => setView('debtors')}
      >
        Clients débiteurs
      </button>
      <button
        type="button"
        className={`pms-chip${view === 'balances' ? ' is-active' : ''}`}
        onClick={() => setView('balances')}
      >
        Suivi des paiements
      </button>
      {flags.allowed && (
        <button
          type="button"
          className={`pms-chip${view === 'controles' ? ' is-active' : ''}`}
          onClick={() => setView('controles')}
        >
          Contrôles{leadCount(flags.findings) > 0 ? ` (${leadCount(flags.findings)})` : ''}
        </button>
      )}
    </div>
  );

  return (
    <CardFindingsFlags findings={flags.findings}>
    <section className="pms">
      {view === 'controles' ? (
        <PerEtablissement>
          <ControlesView day={findingsDay} onDay={setFindingsDay} today={today} nav={nav} />
        </PerEtablissement>
      ) : view === 'arrivals' ? (
        <Arrivals
          briefing={arrivals}
          takings={takings}
          nav={nav}
          form={form}
          onBill={(target, cents) => prefill(target, cents, 'arrival')}
          canBill={canWrite}
          onSettle={settleSuspense}
          onCancelSuspense={(id) => void cancelSuspense(id)}
          refreshKey={refreshKey}
        />
      ) : view === 'departures' ? (
        <Departures
          briefing={departures}
          takings={takings}
          nav={nav}
          form={form}
          onBill={(target, cents) => prefill(target, cents, 'departure')}
          canBill={canWrite}
          onSettle={settleSuspense}
          onCancelSuspense={(id) => void cancelSuspense(id)}
          refreshKey={refreshKey}
        />
      ) : view === 'journee' ? (
        <Journee arrivals={arrivals} departures={departures} takings={takings} nav={nav} />
      ) : view === 'debtors' ? (
        <Debtors briefing={departures} nav={nav} form={form} />
      ) : view === 'balances' ? (
        <PaymentFollowUp
          briefing={arrivals}
          nav={nav}
          form={form}
          onBill={prefillBooking}
          canBill={canWrite}
          refreshKey={refreshKey}
        />
      ) : (
        <Unissued briefing={departures} nav={nav} form={form} />
      )}
    </section>
    </CardFindingsFlags>
  );
}
